import { authService } from './auth';
import { resolveApiErrorMessage } from './api-errors';

export const API_BASE = (window as any).__RC_API_BASE__ || 'http://localhost:7071/api';

export function resolveApiUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  if (pathOrUrl.startsWith('/')) {
    return `${API_BASE}${pathOrUrl}`;
  }

  return `${API_BASE}/${pathOrUrl}`;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: any;

  constructor(_message: string, status: number, code?: string, details?: any) {
    super(resolveApiErrorMessage({ code }, status));
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface DownloadedFile {
  blob: Blob;
  fileName: string;
  contentType: string;
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = authService.accessToken;
  if (!token) {
    authService.logout();
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    authService.logout();
    throw new Error('Unauthorized');
  }

  if (response.status === 402) {
    const error = await response.json().catch(() => ({ error: 'Payment required' }));
    if (error?.code === 'trial_expired') {
      const redirectPath = error?.details?.redirectPath || '/profile/?tab=subscription';
      const currentPath = `${window.location.pathname}${window.location.search}`;
      if (currentPath !== redirectPath && !window.location.pathname.startsWith('/pricing/')) {
        window.location.href = redirectPath;
      }
    }

    throw new ApiError(error.error || 'Payment required', response.status, error.code, error.details);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (response.status === 403) {
    const error = await response.clone().json().catch(() => ({ error: 'Forbidden' }));
    if (error?.code === 'platform_country_not_allowed') {
      authService.logoutWithNotice(error.code);
      throw new ApiError(error.error || 'Forbidden', response.status, error.code, error.details);
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(error.error || `HTTP ${response.status}`, response.status, error.code, error.details);
  }

  return response.json();
}

export async function publicRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(resolveApiUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(error.error || `HTTP ${response.status}`, response.status, error.code, error.details);
  }

  return response.json();
}

export async function authorizedFetch(pathOrUrl: string, options: RequestInit = {}): Promise<Response> {
  const token = authService.accessToken;
  if (!token) {
    authService.logout();
    throw new Error('Not authenticated');
  }

  const response = await fetch(resolveApiUrl(pathOrUrl), {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    authService.logout();
    throw new Error('Unauthorized');
  }

  if (response.status === 403) {
    const error = await response.clone().json().catch(() => ({ error: 'Forbidden' }));
    if (error?.code === 'platform_country_not_allowed') {
      authService.logoutWithNotice(error.code);
      throw new ApiError(error.error || 'Forbidden', response.status, error.code, error.details);
    }
  }

  return response;
}

export async function fetchAuthorizedFile(pathOrUrl: string, options: RequestInit = {}): Promise<DownloadedFile> {
  const response = await authorizedFetch(pathOrUrl, options);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(error.error || `HTTP ${response.status}`, response.status, error.code, error.details);
  }

  const blob = await response.blob();
  const contentType = response.headers.get('Content-Type') || blob.type || 'application/octet-stream';
  const header = response.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/i.exec(header);
  return {
    blob,
    fileName: match?.[1] || 'download',
    contentType,
  };
}

export async function downloadAuthorizedFile(pathOrUrl: string, preferredFileName?: string): Promise<void> {
  const file = await fetchAuthorizedFile(pathOrUrl);
  const fileName = preferredFileName || file.fileName;
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
